import { IconButton, statusColors } from '@nexelem/components';
import { Collapse, ListItemAvatar, ListItemText, styled, useTheme } from '@nexelem/deprecated-theme';
import { ProLightEllipsis } from '@nexelem/icons/ProLightEllipsis';
import { cn } from '@nexelem/utils';
import {
  type Dispatch,
  forwardRef,
  type ReactNode,
  type SetStateAction,
  type SyntheticEvent,
  useMemo,
  useRef,
} from 'react';
import type { AvailableTaskTypes, DISPLAY_FIELD } from '../../types';
import { ProLightSquareMinus } from '@nexelem/icons/ProLightSquareMinus';
import { ProLightSquarePlus } from '@nexelem/icons/ProLightSquarePlus';
import { useDependenciesResize } from './hooks/useDependenciesResize';
import { getTaskStatusColor } from '../../utils';
import { ToDoCheckbox } from '../ToDoCheckbox';
import { type TaskTree, type TaskTreeItem, ToDoTreeType } from '@nexelem/graphne-api';
import { StyledTreeListItem } from './components/StyledTreeListItem';
import { ELEMENTS_GAP, ITEM_INDENT } from './consts';
import { HorizontalLine } from './components/HorizontalLine';
import { VerticalLine } from './components/VerticalLine';
import { NodeIcon } from '../../../ProductionPlans/Gantt/components';
import { faMessageLines } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIsGanttSimulation } from '../../../ProductionPlans/GanttSimulation/hooks/GanttSimulationContext';

const StyledExpandButton = styled(IconButton, {
  shouldForwardProp: (propName: PropertyKey): propName is PropertyKey =>
    !['hasDependencies'].includes(propName as string),
})<{ hasDependencies: boolean }>(({ hasDependencies }) => {
  return {
    visibility: hasDependencies ? 'visible' : 'hidden',
  };
});

const StyledCollapse = styled(Collapse)(({ in: isExpanded }) => {
  return {
    overflow: isExpanded ? 'visible' : 'hidden',
  };
});

export type TreeListNodeItemProps = {
  readonly task: TaskTree | TaskTreeItem;
  readonly isChecked: boolean;
  onMoreClick: (evt: SyntheticEvent, task: AvailableTaskTypes) => void;
  setChecked: (evt: SyntheticEvent) => void;
  readonly isIntermediate?: boolean;
  readonly isCheckable?: boolean;
  getSecondary?: (task: AvailableTaskTypes) => ReactNode;
  readonly children?: ReactNode;
  readonly depth?: number;
  readonly hasDependencies?: boolean;
  readonly notPlannedDependencies?: number[];
  readonly isExpanded: boolean;
  readonly onExpand: Dispatch<SetStateAction<boolean>>;
  readonly isParentExpanded?: boolean;
  readonly index: number;
  readonly displayField: DISPLAY_FIELD;
};

export const TreeListNodeItem = forwardRef<HTMLDivElement, TreeListNodeItemProps>(
  (
    {
      task,
      onMoreClick,
      setChecked,
      getSecondary = () => null,
      children = undefined,
      hasDependencies = false,
      depth = 0,
      isChecked,
      isIntermediate = false,
      isExpanded,
      onExpand,
      isParentExpanded = false,
      index = 0,
      displayField,
    }: TreeListNodeItemProps,
    ref,
  ) => {
    const isSimulation = useIsGanttSimulation();
    const { spacing } = useTheme();
    console.log('spacing(2) = factor(2) * theme.spacing unit(4px) =', spacing(2));

    const internalClickMore = (evt: SyntheticEvent) => onMoreClick(evt, task);
    const toggleExpand = (evt: SyntheticEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onExpand((prev) => !prev);
    };

    const nodeContainerRef = useRef<HTMLDivElement>(null);
    const dependenciesContainerRef = useRef<HTMLDivElement>(null);
    const { dependenciesContainerHeight } = useDependenciesResize(nodeContainerRef, dependenciesContainerRef);
    const taskStatusColor = useMemo(() => getTaskStatusColor(task.status) || 'grey', [task.status]);

    const taskProgress = useMemo(() => {
      if (task.progress) {
        return {
          current: task.progress.current,
          total: task.progress.totalAmount.amount,
          unit: task.progress.totalAmount.unit.name,
        };
      } else {
        return undefined;
      }
    }, [task, task.progress?.totalAmount, task.progress?.current]);

    const isOrder = useMemo(() => (task as TaskTree).treeType === ToDoTreeType.DispatchOrder, [task]);

    return (
      <div
        className="node-container min-w-full"
        ref={nodeContainerRef}
        style={{ width: isExpanded ? 'max-content' : '100%' }}
      >
        <StyledTreeListItem
          divider
          checked={isChecked}
          depth={depth}
          isExpanded={isExpanded}
          secondaryAction={!isOrder && <IconButton Icon={ProLightEllipsis} size="small" onClick={internalClickMore} />}
        >
          <div className="flex items-center justify-start" style={{ gap: (1 + depth * ITEM_INDENT) * 4 }}>
            <ListItemAvatar>
              <div className="flex items-center justify-center pl-1" ref={ref}>
                {isSimulation && (
                  <ToDoCheckbox checked={isChecked} isIntermediate={isIntermediate} onChange={setChecked} />
                )}
              </div>
            </ListItemAvatar>
            <div className="flex items-center" style={{ gap: ELEMENTS_GAP * 4 }}>
              <div className="flex items-center gap-0.5">
                <div className="list-line-container relative">
                  {depth > 0 && (
                    <HorizontalLine
                      hasDependencies={hasDependencies}
                      index={index}
                      isParentExpanded={isParentExpanded}
                    />
                  )}
                  <StyledExpandButton
                    hasDependencies={hasDependencies}
                    Icon={isExpanded ? ProLightSquareMinus : ProLightSquarePlus}
                    size="small"
                    onClick={toggleExpand}
                  />
                </div>
                <NodeIcon
                  dueDate={task.dueDate ? new Date(task.dueDate) : undefined}
                  isOrder={isOrder}
                  statusColor={taskStatusColor}
                  taskType={task.taskType}
                />
              </div>
              <ListItemText
                disableTypography
                secondary={getSecondary(task)}
                primary={
                  <div className="flex gap-2">
                    {!!task.comments?.length && (
                      <FontAwesomeIcon className="text-text-secondary-darker" icon={faMessageLines} />
                    )}
                    {taskProgress && (
                      <span className={cn('shrink-0 text-s11-sb', statusColors({ dotTextText: taskStatusColor }))}>
                        {taskProgress.current}/{taskProgress.total} {taskProgress.unit}
                      </span>
                    )}
                    <span className="overflow-hidden text-s11-sb text-ellipsis whitespace-nowrap">
                      {isOrder ? `${task.name} ${(task as TaskTree).supplierOrderNumber}` : task[displayField]}
                    </span>
                  </div>
                }
              />
            </div>
          </div>
        </StyledTreeListItem>
        {hasDependencies && (
          <div className="dependenciesContainer relative" ref={dependenciesContainerRef}>
            <StyledCollapse easing="ease" in={isExpanded} timeout={300}>
              {children}
              <VerticalLine dependenciesContainerHeight={dependenciesContainerHeight} depth={depth} />
            </StyledCollapse>
          </div>
        )}
      </div>
    );
  },
);

TreeListNodeItem.displayName = 'NodeItemList';
